import { AutoDataPrefixer } from "./AutoDataPrefixer";
import { CDParser } from "../css-definitions/CDParser";
import { CDSelector } from "../css-definitions/CDSelector";
import { CDElement } from "../css-definitions/CDElement";
import { CDText } from "../css-definitions/CDText";
import { RegExpUtility } from "../text/RegExpUtility";
import { hasClass } from "./Classes";
import { LinkEntry } from "./LinkEntry";
import { DOMNameSpaces } from "./DOMNameSpaces";
import { ElementAttribute } from "./ElementAttribute";
import { Arrays } from "../tools/Arrays";
import { MultiString } from "../i18n/MultiString";
import { ElementType } from "./ElementType";
import { Range } from "../geometry/Range";

export class HTMLEditor 
{   

  static addSeperator( elements:Node[], seperatorCreator:()=>Node )
  {
    let output:Node[] = [];

    for ( let i = 0 ; i < elements.length; i++ )
    {
      if ( i !== 0 ) 
      {
        output.push( seperatorCreator() );
      }

      output.push( elements[ i ] );
    }


    return output;
  }

  static setFirst( element:Element )
  {
    element.parentElement.insertBefore( element.parentElement.firstChild, element );
  }

  static setLast( element:Element )
  {
    element.parentElement.appendChild( element );
  }

  static forAllChildren( element:Element, callback:( node:Node ) => void )
  {
    for ( let i = 0 ; i< element.childNodes.length; i++ )
    {
      callback( element.childNodes[ i ] );
    }
  }

  static getChildren( element:Element ):Node[]
  {
    let nodes:Node[] = [];

    HTMLEditor.forAllChildren( element, c => nodes.push( c ) );

    return nodes;
  } 

  static hide( element:Element )
  {
    let htmlElement = element as HTMLElement;

    htmlElement.style.display = "none";
  }

  static show( element:Element, displayValue:"block"|"inline-block"|"inline" = "block" )
  {
    let htmlElement = element as HTMLElement;

    htmlElement.style.display = displayValue;
  }

  static setVisibility( element:Element, visible:boolean,  displayValue:"block"|"inline-block"|"inline" = "block" )
  {
    if ( visible )
    {
      this.show( element, displayValue );
    }
    else
    {
      this.hide( element );
    }
  }


  static filterChildren( element:HTMLElement, filter:( node:Node ) => boolean )
  {
    let filteredNodes:Node[] = [];

    let childNodes = element.childNodes;

    for ( let i = 0; i < childNodes.length; i++ )
    {
      if ( filter( childNodes[ i ] ) )
      {
        filteredNodes.push( childNodes[ i ] );
      } 
    } 

    return filteredNodes;
  }

  static checkFastIfVisible( element:HTMLElement  )
  {
    return element.offsetParent !== null;
  }

  static setAsOnlyChild( element:Element, child:Element )
  { 
    element.innerHTML = "";
    element.appendChild( child );
  }

  static fillRect( element:HTMLElement, rect:DOMRect|ClientRect, offsetX=0, offsetY=0 , positionMode:"absolute"|"fixed"|"relative"|null ="absolute")
  {
    if ( positionMode )
    {
      element.style.position = positionMode;
    }

    let leftValue = `calc( ${ rect.left + offsetX } * 1px - var( --sx ) )`;
    let topValue   = `calc( ${ rect.top  + offsetY } * 1px )`;

    element.style.left = leftValue;
    element.style.top  = topValue;

    element.style.width = `calc( ${ rect.width } * 1px )`;
   // element.style.height  = rect.height  + "px";

    //console.log( "FILLRECT", leftValue, topValue );
  }

  static remove( node:Node )
  {
    if ( ! node )
    {
      return;
    }

    if ( ! node.parentNode )
    {
      return;
    }

    node.parentNode.removeChild( node );
  }

  static removeChildren( parent:Element ):Node[]
  {
    let nodes:Node[] = [];

    for ( let i = 0; i < parent.childNodes.length; i++ )
    {
      let child = parent.childNodes[ i ];
      nodes.push( child );
    }

    parent.innerHTML = "";
    return nodes;
  }
  
  static _cssCreationRegex:RegExp;

  static toggleClass( element:Element, className:string )
  {
    let flag = ! hasClass( element, className );
    HTMLEditor.setClass( element, className, flag );
  }

  static onClick( element:Element, callback:()=>any )
  {
    element.addEventListener( "click", callback );
    //element.addEventListener( "mousedown", callback );
    //lement.addEventListener( "touchstart", callback );
  }

  static assignText( rootElement:Element, data:{[selector:string]:string|number|LinkEntry })
  {
    if ( ! rootElement || ! data )
    {
      return;
    } 

    for ( let selector in data )
    {
      if ( data.hasOwnProperty( selector ) )
      {
        let element = rootElement.querySelector( selector );

        if ( ! element )
        {
          continue;
        }

        element.innerHTML = "";

        let value = data[ selector ];

        if ( typeof( value ) === "string" || typeof( value ) === "number" )
        { 
          element.appendChild( rootElement.ownerDocument.createTextNode( data[ selector ] + "" ) );
        }
        else if ( value && value.url && value.name )
        {
          let linkEntry = value as LinkEntry;
          element.setAttribute( "href", linkEntry.url );
          element.appendChild( rootElement.ownerDocument.createTextNode( linkEntry.name) );
        }
        else if ( value === null || value === undefined )
        {
          element.appendChild( rootElement.ownerDocument.createTextNode( "(---)" ) );
        }
        
      }
    }
  }

  static hasClass( element:Element, classNameValue:string )
  {
    let classAttribute = element.getAttribute( "class" ) || "";
    let classNameValues = classAttribute.split( /\s+/ );
    let index = classNameValues.indexOf( classNameValue );
    
    return index >= 0;
  }

  static hasAttributeFlag( element:Element, elementAttribute:ElementAttribute, flag:string )
  {
    let attributeFlags = elementAttribute.from( element );

    let matcher = RegExpUtility.createRegExp( /\bclassName\b/, "className", flag );
    return matcher.test( attributeFlags );
  }
  
  static toggleAttributeFlag( element:Element, elementAttribute:ElementAttribute, flag:string )
  {
    let hasFlag = HTMLEditor.hasAttributeFlag( element, elementAttribute, flag );
    HTMLEditor.setAttributeFlag( element, elementAttribute, flag, ! hasFlag )
  }

  static setAttributeFlag( element:Element, elementAttribute:ElementAttribute, flag:string, active:boolean = true )
  {
   
    let attributeFlags = elementAttribute.from( element );
    let flags = attributeFlags.split( " " );
    let flagIndex = flags.indexOf( flag );

    let flagIsActive = flagIndex !== -1;

    let cachedFlags = attributeFlags;
    
    if ( flagIsActive === active )
    {
      return;
    }

    if ( active )
    {
      if ( attributeFlags.length != 0 )
      {
        attributeFlags += " ";
      }

      attributeFlags += flag;
    }
    else
    {
      Arrays.removeAt( flags, flagIndex );
      
      attributeFlags = flags.join( " " );        
    }

    elementAttribute.to( element, attributeFlags );

    
  }

  static setClass( element:Element, className:string, active:boolean = true, nameSpace:string = null )
  {
    let useNameSpace = nameSpace != null;

    if ( useNameSpace && nameSpace === "" )
    { 
      nameSpace = null; 
    }

    let classAttribute = 
      ( 
        useNameSpace ? 
        element.getAttributeNS( nameSpace, "class" ): 
        element.getAttribute( "class" ) 
      )      
      || "";

    let classes = classAttribute.split( " " );
    
    for ( let i = 0; i < classes.length; i++ )
    {
      if ( classes[ i ] === className )
      {
        if ( active )
        {
          return;
        }
        else
        {
          Arrays.removeAt( classes, i );
          i = classes.length;
          classAttribute = classes.join( " " );
        }
      }
    }

    if ( active )
    {
      if ( ! /^\s*$/.test( classAttribute ) )
      {
        classAttribute += " ";
      }
      
      classAttribute += className;
    }
    

    if ( useNameSpace )
    {
      element.setAttributeNS( nameSpace, "class", classAttribute );
    }
    else
    {
      element.setAttribute( "class", classAttribute );
    }
      
    

    
  }

  static appendText( destination:Element, source:string|Element, sourceAttribute:string ) 
  {
    if ( sourceAttribute ) 
    {
      sourceAttribute = AutoDataPrefixer.apply( sourceAttribute );
      HTMLEditor.appendTextFromAttribute( destination, source as Element, sourceAttribute );
    }
    else 
    {
      if ( typeof source === "string" ) 
      {
        destination.appendChild( document.createTextNode( source ) );
      }
      else 
      {
        HTMLEditor.appendTextFromTextContent( destination, source );
      }
    }
  } 

  static setHighlightedTextForRange( destination:Element, text:string, range:Range, type:ElementType<any>, doc:Document = null )
  {
    doc = doc || document;
    destination.innerHTML = "";

    let chopped = RegExpUtility.chopRange( text, range.min, range.max );

    console.log( "CHOPPED", chopped );

    if ( chopped[ 0 ] )
    {
      destination.appendChild( doc.createTextNode( chopped[ 0 ] ) );
    }
    
    if ( chopped[ 1 ] )
    {
      destination.appendChild( type.create( chopped[ 1 ], doc ) );
    }

    if ( chopped[ 2 ] )
    {
      destination.appendChild( doc.createTextNode( chopped[ 2 ] ) );
    }
  }

  static setHighlightedText( destination:Element, text:string, matcher:RegExp, type:ElementType<any>, doc:Document = null )
  {    
    let globalRegex = RegExpUtility.makeIgnoreCase( RegExpUtility.makeGlobal( matcher ) );

    doc = doc || document;
    let matches = [...text.matchAll( globalRegex )];

    destination.innerHTML = "";
    
    if ( matches.length === 0 )
    {
      destination.appendChild( doc.createTextNode( text ) );
      
      return;
    }    

    let unselectedIndex = 0;

    console.log( "text", text );
    for ( let i = 0; i < matches.length; i++ )
    {
      let match = matches[ i ];

      if  ( match.index > unselectedIndex )
      {
        let unselectedText = text.substring( unselectedIndex, match.index );
        destination.appendChild( doc.createTextNode( unselectedText ) );
      }

      let end = match.index + match[ 0 ].length;

      
      let selectedText = text.substring( match.index, end );

      console.log( "MATCH", match.index, match[ 0 ].length, selectedText );
      destination.appendChild( type.create( selectedText, doc ) );

      unselectedIndex = end;
    }

    

    let lastMatch = matches[ matches.length - 1 ];
    let lastEnd = lastMatch.index + lastMatch[ 0 ].length;

    if ( lastEnd < text.length )
    {
      let unselectedText = text.substring( lastEnd, text.length );
      destination.appendChild( doc.createTextNode( unselectedText ) );
    }
    

    
  }

  static setText( destination:Element, source:string|Element, sourceAttribute?:string ) 
  {
    destination.innerHTML = "";


    if ( sourceAttribute ) 
    {
      sourceAttribute = AutoDataPrefixer.apply( sourceAttribute );
      HTMLEditor.appendTextFromAttribute( destination, source as Element, sourceAttribute );
    }
    else 
    {
      if ( typeof source === "string") 
      {        
        destination.appendChild( document.createTextNode( source ) );
      }
      else 
      {
        HTMLEditor.appendTextFromTextContent( destination, source );
      }
    }
  }

  static escapeToInnerHTML( text:string, doc:Document = null  )
  {
    doc = doc || document;
    let temporary = doc.createElement( "div" );
    temporary.appendChild( doc.createTextNode( text ) );
    return temporary.innerHTML;
  }

  static appendTextFromAttribute(destination:Element, source:Element, sourceAttribute:string) 
  {
    sourceAttribute = AutoDataPrefixer.apply(sourceAttribute);
    let text = source.getAttribute(sourceAttribute);
    destination.appendChild(document.createTextNode(text));
  }

  static appendTextFromTextContent(destination:Element, source:Element) 
  {
    let text = source.textContent;
    destination.appendChild(document.createTextNode(text));
  }

  static copyAttribute( destination:Element, source:Element, sourceAttribute:string) 
  {
    sourceAttribute = AutoDataPrefixer.apply( sourceAttribute );
    let value = source.getAttribute (sourceAttribute );
    destination.setAttribute( sourceAttribute, value );
  }
  static copyAllAttributesThroughRawHTML( source:Element, destination:Element )
  {
    let attributes:[string,string][] = [];

    for ( let i = 0; i < destination.attributes.length; i++ )
    {
      let attribute = destination.attributes[ i ];
      let attributeName = attribute.nodeName;
      let attributeValue = attribute.nodeValue;

      attributes.push( [ attributeName, attributeValue ] );
    }

    for ( let i = 0; i < source.attributes.length; i++ )
    {
      let attribute = source.attributes[ i ];
      let attributeName = attribute.nodeName;
      let attributeValue = attribute.nodeValue;

      let index = attributes.findIndex( a => a[ 0 ] === attributeName );
      
      if ( index === -1 )
      {
        attributes.push( [ attributeName, attributeValue ] );
      }
      else
      {
        attributes[ index ][ 1 ] = attributeValue;
      }
    } 

    let innerHTML = destination.innerHTML;
    let attributesString = "";

    for ( let i = 0; i < attributes.length; i++ )
    {
      if ( i !== 0 ){ attributesString += " "; }

      let attribute = attributes[ i ];
      attributesString += `${attribute[ 0 ]}="${attribute[ 1 ]}"`;
    }

    let tag = destination.nodeName.toLowerCase();

    let html = `<${tag} ${attributesString}>${innerHTML}</${tag}>`;

    console.log( "Copied html:", html );

    destination.outerHTML = html;

    return destination;

  }

  static copyAllAttributes( source:Element, destination:Element )
  {
    for ( let i = 0; i < source.attributes.length; i++ )
    {
      let attribute = source.attributes[ i ];
      let attributeName = attribute.nodeName;
      let attributeValue = attribute.nodeValue;      
      destination.setAttribute( attributeName, attributeValue );
    }
  }

  static cloneChildren( element:Element ):Node[]
  {
    let clones:Node[] = [];

    for ( let i = 0; i < element.childNodes.length; i++ )
    {
      
      clones.push( element.childNodes[ i ].cloneNode( true ) );
    }

    
    return clones;
  }

  static get cssCreationRegex() 
  {
    if ( HTMLEditor._cssCreationRegex) 
    {
      return HTMLEditor._cssCreationRegex;
    }

    let cssCreationRules = [
      //"Attribute",
      /\[\s*(?:\w|-)+\s*(?:(?:\~|\||\^|\$|\*)?=)\s*(?:(?:"(?:\\"|.)*?")|(?:'(?:\\'|.)*?')|(?:(?:\w|-)+))\s*\]/,
      //"ID",
      /#(?:\w|-)+/,
      //"Class",
      /\.(?:\w|-)+/,
      //"Empty Attribute",
      /\[\s*(?:\w|-)+\s*]/,
      //"Element",
      /(?:\w|-)+/
    ];

    let regexString = "";

    for (let i = 0; i < cssCreationRules.length; i++) 
    {
      if (i !== 0) 
      {
        regexString += "|";
      }

      regexString += "(" + cssCreationRules[i].source + ")";
    }

    HTMLEditor._cssCreationRegex = new RegExp(regexString, "g");

    return HTMLEditor._cssCreationRegex;
  }

  static create(cssSource:string) 
  {
    if ("string" === typeof cssSource) 
    {
      return HTMLEditor.createElementFromCSSSelector(cssSource);
    }

    return HTMLEditor.cssToHTML(cssSource);
  }

  static createElementFromCSSSelector(cssString:string) 
  {
    let useNameSpace = false;
    let nameSpace:string = null;

    if ( cssString.length > 0 && cssString[ 0 ] === ( "\"" ) )
    {
      let nameSpaceEnd = cssString.indexOf( "\"", 1 );

      if ( nameSpaceEnd !== -1 )
      {
        let nameSpaceValue = cssString.substring( 1, nameSpaceEnd );
        
        switch( nameSpaceValue )
        {
          case "SVG": nameSpace = DOMNameSpaces.SVG; break;
          case "XLink": nameSpace = DOMNameSpaces.XLink; break;
          case "": nameSpace = null; break;
          default: nameSpace = nameSpaceValue; break;
        }                
  
        cssString = cssString.substring( nameSpaceEnd + 1 );
        useNameSpace = true;
      }
      else
      {
        console.log( "Expected namespace definition, closer not found", cssString );
      }

    }

    let type = "div";
    let classes = [];
    let id = null;
    let atts = [];
    let regex = HTMLEditor.cssCreationRegex;
    let result;

    while ((result = regex.exec(cssString)) !== null) 
    {
      for (let i = 1; i < result.length; i++) 
      {
        if (result[i]) 
        {
          let value = result[0];

          switch (i) 
          {
            case 1:
            {
              value = AutoDataPrefixer.apply(value);
              value = value.replace(/^\s*\[\s*/, "");
              value = value.replace(/\s*\]\s*$/, "");
              let attribute = value.split(/(?:\~|\||\^|\$|\*)?=/);
              let attributeName = attribute[0];
              attributeName = attributeName.replace(/^\s/, "");
              attributeName = attributeName.replace(/\s$/, "");
              let attributeValue = attribute[1];
              attributeValue = attributeValue.replace(/^('|")/, "");
              attributeValue = attributeValue.replace(/('|")$/, "");
              atts.push({ name: attributeName, value: attributeValue });
            }
            break;

            case 2:
            {
              id = value.substring(1);
            }
            break;

            case 3:
            {
              classes.push(value.substring(1));
            }
            break;

            case 4:
            {
              let name = value;
              name = AutoDataPrefixer.apply(name);
              name = name.replace(/^\s*\[\s*/, "");
              name = name.replace(/\s*\]\s*$/, "");
              atts.push({ name: name, value: "" });
            }
            break;

            case 5:
            {
              type = value;
            }
            break;
          }

          i = result.length;
        }
      }
    }

    let element = 
      useNameSpace ? document.createElementNS( nameSpace, type ) : document.createElement( type );

    if (id !== null) 
    {
      element.setAttribute("id", id);
    }

    if (classes.length > 0) 
    {
      element.setAttribute("class", classes.join(" "));
    }

    for (var i = 0; i < atts.length; i++) 
    {
      element.setAttribute(atts[i].name, atts[i].value);
    }

    return element;
  }

  static replace(oldNode:Node, newNode:Node) 
  {
    oldNode.parentNode.replaceChild( newNode, oldNode );
  }

  
  static splitText( element:Element, characterElement:string = "span" )
  {
    let children = HTMLEditor.nodeListToArray( element.childNodes );

    let allCharacters:Node[] = [];

    for ( let i = 0; i < children.length; i++ )
    {
      if ( children[ i ].nodeType !== Node.TEXT_NODE )
      {
        continue;
      }
      
      let characters = Array.from( children[ i ].textContent );

      let before = children[ i ].previousSibling;

      element.removeChild( children[ i ] );

      let beforeElement = before;

      characters.forEach(
        ( c )=>
        {
          let textNode = document.createElement( characterElement );
          textNode.appendChild( document.createTextNode( c ) );

          HTMLEditor.insertAfter( element, beforeElement, textNode );
          
          beforeElement = textNode;
          allCharacters.push( textNode );
        }
      );
    }

    return allCharacters;
  }

  static wrapElement( node:Node, cssString:string ) 
  {
    let wrapper = HTMLEditor.createElementFromCSSSelector( cssString );
    wrapper.appendChild( node );
    return wrapper;
  }

  static wrapSelection( root: HTMLElement, wrapperTag: string ): HTMLElement | null 
  {
    let selection = window.getSelection();

    if ( ! selection || selection.rangeCount === 0 )
    { 
      return null;      
    }

    let range = selection.getRangeAt( 0 );

    if ( ! root.contains( range.commonAncestorContainer ) ) 
    {
      return null;
    }

    root.normalize();
  
    let clone = range.cloneRange();
  
    let extractedContent = clone.extractContents();
  
    let wrapper = document.createElement( wrapperTag );
    wrapper.appendChild( extractedContent );
  
    clone.insertNode( wrapper );
  
    selection.removeAllRanges();
    let afterWrapper = document.createRange();
    afterWrapper.setStartAfter( wrapper );
    afterWrapper.collapse( true );
    selection.addRange( afterWrapper );
  
    return wrapper;
  }

  static unwrap( element:Element )
  {
    let parent = element.parentElement;
    let children = HTMLEditor.getChildren( element );
    children.forEach( c => parent.insertBefore( c, element ) );
    parent.removeChild( element );
  }

  
  static querySelectInParents( node:Node, selector:string )
  {
    var it = node.parentElement;
    
    while ( it != null )
    {
      let result = it.querySelector( selector );

      if ( result )
      {
        return result;
      }
      
      it = it.parentElement;
    }
  }

  static findParent( node:Node, selector:string )
  {
    var it = node.parentElement;
    
    while ( it != null )
    {
      if ( it.matches( selector ) )
      {
        return it;
      }
      
      it = it.parentElement;
    }

    return null;
  }

  static wrapReplace( node:Node, cssString:string )
  {
    if ( ! node.parentNode )
    {
      return HTMLEditor.wrapElement( node, cssString );
    }

    let wrapper = HTMLEditor.createElementFromCSSSelector( cssString );
    node.parentNode.insertBefore( wrapper, node );
    wrapper.appendChild( node );
    return wrapper;
  }
  
  static wrapWithAnchorHref( node:Node, href:string )
  {
    let anchor = ElementType.anchor.create();
    anchor.href = href;

    return HTMLEditor.wrapWith( node, anchor );
  }

  static wrapWith( node:Node, wrapper:Element )
  {
    if ( ! node.parentNode )
    {
      wrapper.appendChild( node );
      return;
    }

    node.parentNode.insertBefore( wrapper, node );
    wrapper.appendChild( node );
    return wrapper;

  }

  

  

  static cssToHTML(css:any, cssSelector?:string) 
  {
    let parser = new CDParser(css);

    if ( ! cssSelector ) 
    {
      let firstSelector = parser.root.firstSelector;
      return HTMLEditor.cdSelectorToHTML( firstSelector );
    }

    cssSelector = AutoDataPrefixer.apply( cssSelector );
    let cdSelectors = parser.root.$$( CDElement.CDSelectorType );

    for ( let cdSelector of cdSelectors ) 
    {
      if ( cssSelector === cdSelector.selector ) 
      {
        return HTMLEditor.cdSelectorToHTML( cdSelector );
      }
    }

    return null;
  }

  static cdSelectorToHTML( cdSelector:CDSelector ) 
  {
    if ( ! cdSelector ) 
    {
      return null;
    }

    let element = HTMLEditor.createElementFromCSSSelector( cdSelector.selector );

    let children = cdSelector.children;

    for ( let child of children ) 
    {
      if ( child.isSelector ) 
      {
        let selectorChild = child as CDSelector;

        if ( selectorChild.isHTMLCreationChild ) 
        {
          let childElement = HTMLEditor.cdSelectorToHTML( selectorChild );
          element.appendChild( childElement );
        }
      }
      else if ( child.isText ) 
      {

        let textChild = child as CDText;

        if ( textChild.isHTML )
        {
          element.innerHTML = textChild.textContent;
        }
        else
        {
          let textNode = document.createTextNode( textChild.textContent );
          element.appendChild( textNode );
        } 

      }
    }

    return element;
  }

  static renameCustomElement( node:Element )
  {
    let name = node.tagName;

    if ( /-/.test( name ) ) 
    {
      return HTMLEditor.rename( node, "[" + name + "]" );
    }

    return node;
  }

  static mergeAnchors( parent:Element, childStart:number, childEnd:number )
  {
    let contentNodes = [];

    for ( let i = childStart; i <= childEnd; i++ )
    {
      var anchorWrapper = parent.children[ i ];
      
      for ( let j = 0; j < anchorWrapper.children.length; j++ )
      {
        contentNodes.push( anchorWrapper.children[ j ] );
      }

      anchorWrapper.innerHTML = "";
    }

    for ( let i = childEnd; i > childStart; i-- )
    {
      HTMLEditor.remove( parent.children[ i ] );
    }

    contentNodes.forEach( c => parent.children[ childStart ].appendChild( c ) );
  }

  static nodeListToArray( list:NodeList ):Element[]
  {
    let elementArray = Array.prototype.slice.call(list);
    return elementArray;
  }

  static queryAll( element:Element|Document, selector:string )
  {
    return HTMLEditor.nodeListToArray( element.querySelectorAll( selector ) );
  }

  static forAll( element:Element|Document, selector:string, action:( e:Element )=>void )
  {
    HTMLEditor.queryAll( element, selector ).forEach( e => action( e ) );
  }

  static rename( node:Element, cssString:string ) 
  {
    let renamed = HTMLEditor.createElementFromCSSSelector(cssString);
    let children = HTMLEditor.nodeListToArray(node.childNodes);

    for ( let i = 0; i < node.attributes.length; i++ ) 
    {
      renamed.setAttribute( node.attributes[i].name, node.attributes[i].value );
    }

    for ( let child of children ) 
    {
      renamed.appendChild( child );
    }

    HTMLEditor.replace( node, renamed );

    return renamed;
  }

  static addOnStart( parent:Element, node:Element )
  {
    if ( parent.childNodes.length == 0 )
    {
      parent.appendChild( node );
      return;
    }

    parent.insertBefore( node, parent.firstChild )

  }

  static insertBefore( parentElement:Element, nodeBefore:Node, nodeAfter:Node ) 
  {
    parentElement.insertBefore( nodeBefore, nodeAfter );
  }

  static insertAfter( parentElement:Element, nodeBefore:Node, nodeAfter:Node ) 
  {
    if ( ! nodeBefore )
    {
      if ( parentElement.childNodes.length == 0 )
      {
        parentElement.appendChild( nodeAfter );
      }
      else
      {
        parentElement.insertBefore( nodeAfter, parentElement.firstChild );
      }

      return;
    }

    HTMLEditor.addAfter( nodeBefore, nodeAfter );
  }

  static insertBreakBefore( element:Element )
  {
    let br = document.createElement( "br" );

    element.parentElement.insertBefore( br, element );
  }

  static insertBreakAfter( element:Element )
  {
    let br = document.createElement( "br" );

    HTMLEditor.addAfter( element, br );
  }

  static addBefore( nodeBefore:Node, nodeAfter:Node ) 
  {
    nodeAfter.parentElement.insertBefore( nodeBefore, nodeAfter );
  }

  static addAfter( nodeBefore:Node, nodeAfter:Node ) 
  {
    let parent = nodeBefore.parentNode;
    
    if ( ! parent ) 
    {
      return;
    }

    if ( nodeBefore.nextSibling ) 
    {
      parent.insertBefore( nodeAfter, nodeBefore.nextSibling );
    }
    else 
    {
      parent.appendChild( nodeAfter );
    }
  }

  static stringify( node:Element, pretty = true ) 
  {
    if ( ! pretty ) 
    {
      return new XMLSerializer().serializeToString(node);
    }
    else 
    {
      return HTMLEditor.stringifyPretty(node);
    }
  }

  static appendAll(element:Element, nodes:Node[]) 
  {
    for (let i = 0; i < nodes.length; i++) 
    {
      element.appendChild(nodes[i]);
    }
  }

  static stringToNodes(html:string) 
  {
    let wrapper = document.createElement("div");

    wrapper.innerHTML = html;
    let nodes = [];

    for ( let i = 0; i < wrapper.childNodes.length; i++ ) 
    {
      nodes.push(wrapper.childNodes[i]);
    }

    wrapper.innerHTML = "";

    return nodes;
  }

  static stringToWrappedElement( html:string, elementName:string = "div", doc:Document = undefined )
  {
    doc = doc || document; 
    let element = doc.createElement( elementName );
    element.innerHTML = html;
    return element;
  }
  
  static stringToDocument( html:string ):Document
  {    
    let parser = new DOMParser();
    let doc = parser.parseFromString( html, "text/html" );
    return doc;    
  }
  
  
  static stringToElement( html:string ) 
  {
    let nodes = HTMLEditor.stringToNodes( html );

    for ( let node of nodes ) 
    {
      if ( Node.ELEMENT_NODE === node.nodeType ) 
      {
        return node;
      }
    }

    return null;
  }

  static stringifyPretty(node:Element)
  {
    let output:string[] = [];

    HTMLEditor._stringifyPretty(node, output, 0);

    return output.join("");
  }

  static escapeXML(value:string) 
  {
    let escapingSymbolsRegEx = /&|<|>|"|'|/;

    if (escapingSymbolsRegEx.test(value)) 
    {
      value = value.replace(/&/g, "&amp;");
      value = value.replace(/</g, "&lt;");
      value = value.replace(/>/g, "&gt;");
      value = value.replace(/"/g, "&quot;");
      value = value.replace(/'/g, "&apos;");
    }

    return value;
  }

  static setDimensionsFromRect( element:HTMLElement, rect:DOMRect|ClientRect, extension:number = 0 )
  {
    element.style.left = ( rect.left - extension ) + "px";
    element.style.top = ( rect.top - extension ) + "px";
    element.style.width = ( rect.width + extension ) + "px";
    element.style.height = ( rect.height + extension  ) +"px";
  }

  static removeWhiteSpaceNodes( element:Element )
  {
    let children = HTMLEditor.nodeListToArray( element.childNodes );
    
    children.forEach(
      ( childNode )=>
      { 
        let isTextNode = Node.TEXT_NODE !== childNode.nodeType;

        if ( isTextNode )
        {
          return;
        }

        let textValue = childNode.nodeValue;
        let isWhiteSpaceOnly = /(s|\n|\r)*/.test( textValue );

        if ( ! isWhiteSpaceOnly )
        {
          return;
        } 

        HTMLEditor.remove( childNode );

      }
    )
  }

  static setAsLastChild( element:Element )
  {
    let parent = element.parentElement;

    if ( element.parentElement === null )
    {
      return;
    }

    parent.removeChild( element );
    parent.appendChild( element );
  }

  static setAsFirstChild( element:Element )
  {
    let parent = element.parentElement;

    if ( element.parentElement === null )
    {
      return;
    }

    if ( parent.childNodes.length === 1 )
    {
      return;
    }

    parent.removeChild( element );

    parent.insertBefore( element, parent.childNodes[ 0 ] );
  }

  static toInnerHTML( textOrNode:string|Node ):string
  {
    if ( typeof textOrNode === "string" )
    {
      return textOrNode;
    }

    let node = textOrNode as Node;

    if ( Node.TEXT_NODE === node.nodeType )
    {
      return node.textContent;
    }

    let element = textOrNode as Element;

    return element.innerHTML;  
  }
  
  static _stringifyPretty(node:Node, output:string[], indentLevel:number) 
  {
    function indent(indentLevel:number) 
    {
      let indentSymbol = "  ";
      let indent = "";
      for (let i = 0; i < indentLevel; i++) {
        indent += indentSymbol;
      }
      return indent;
    }

    function createStartTag(element:Element) 
    {
      let startTag = element.nodeName;

      for (let i = 0; i < element.attributes.length; i++) 
      {
        startTag += " ";
        let attribute = element.attributes[i];
        let name = attribute.name;
        let value = HTMLEditor.escapeXML(attribute.value);
        startTag += name + "=\"" + value + "\"";
      }

      return startTag;
    }

    switch (node.nodeType) 
    {
      case Node.ELEMENT_NODE:
      {
        let element = node as Element;
        let indentSpace = indent(indentLevel);

        output.push(indentSpace);
        output.push("<" + createStartTag(element) + ">");
        output.push("\n");

        for (let i = 0; i < element.childNodes.length; i++) 
        {
          HTMLEditor._stringifyPretty(element.childNodes[i], output, indentLevel + 1);
        }

        output.push(indentSpace);
        output.push("</" + node.nodeName + ">");
        output.push("\n");
      }
      break;

      case Node.TEXT_NODE:
      {
        let textContent = node.textContent;

        textContent = textContent.replace(/^(\s|\n\r|\n|\r)+/, "");
        textContent = textContent.replace(/(\s|\n\r|\n|\r)+$/, "");

        if (textContent !== "") 
        {
          let lines = textContent.split(/(\n\r|\n|\r)+/);
          let indentSpace = indent(indentLevel + 1);

          output.push(indentSpace);
          output.push(lines.join("\n" + indentSpace));
          output.push("\n");
        }
      }
      break;
    }
  }
}